import { toast } from "sonner";
import { useUpdateOrganization } from "@/api/mutations";
import type { OrganizationDetail } from "@/api/types";
import { usePermissions } from "@/auth/permissions";
import { Section } from "@/components/feedback/key-value";
import { StoreLink } from "@/components/navigation/entity-links";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { Switch } from "@/components/ui/switch";

/**
 * The organization's CRM email switch: when on, each booked appointment is emailed to the store's
 * CRM as an ADF lead (Telnyx Email, to the store's email). `organizations:update` turns it on/off.
 */
export function OrgCrmEmail({ org }: { org: OrganizationDetail }) {
  const { can } = usePermissions();
  const update = useUpdateOrganization(org.id);
  const crm = org.crmEmail ?? { enabled: org.settings.crmEmail === true, configured: true, storesWithoutEmail: [] };
  const toggle = (on: boolean) =>
    update.mutate(
      { crmEmail: on },
      {
        onSuccess: () => toast.success(on ? "CRM email turned on" : "CRM email turned off", { description: org.name }),
        onError: (err) => toast.error("Couldn't change CRM email", { description: err.message }),
      },
    );
  return (
    <Section title="CRM email (ADF)">
      <Field orientation="horizontal" className="items-start gap-3">
        <Switch id="org-crm-email" checked={crm.enabled} disabled={!can("organizations:update") || update.isPending} onCheckedChange={toggle} />
        <div className="space-y-0.5">
          <FieldLabel htmlFor="org-crm-email">Email booked appointments to the store's CRM</FieldLabel>
          <FieldDescription className="text-xs">
            When an appointment is booked, the lead (seller, vehicle, offer and appointment time) is emailed as ADF to the store's email, once per appointment.
            Off: nothing is sent.
          </FieldDescription>
        </div>
      </Field>
      {crm.enabled && !crm.configured ? (
        <Alert variant="destructive" className="mt-3">
          <AlertDescription>CRM emails aren't set up on the platform (CRM_EMAIL_FROM, TELNYX_API_KEY): nothing is sent until they are.</AlertDescription>
        </Alert>
      ) : null}
      {crm.enabled && crm.storesWithoutEmail.length > 0 ? (
        <Alert className="mt-3">
          <AlertDescription>
            <span>These stores have no email, so their appointments aren't sent (store → Edit → Email): </span>
            {crm.storesWithoutEmail.map((s, i) => (
              <span key={s.id}>
                {i > 0 ? ", " : ""}
                <StoreLink store={s} />
              </span>
            ))}
          </AlertDescription>
        </Alert>
      ) : null}
    </Section>
  );
}
